/**
 * CMS Pages Component
 * ZO Bull Algo Trading Platform
 * 
 * Handles: About, Contact, Terms, Privacy, Disclaimer, FAQ, HowTo
 * Content is fetched from CMS API
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './CMSPages.css';

interface PageData {
  slug: string;
  title: string;
  icon?: string;
  description?: string;
  content?: string;
  meta_title?: string;
  meta_description?: string;
  is_published?: boolean;
}

const DEFAULT_PAGES: Record<string, PageData> = {
  about: { slug: 'about', title: 'About Us', icon: '🏢' },
  contact: { slug: 'contact', title: 'Contact Us', icon: '📧' },
  terms: { slug: 'terms', title: 'Terms of Service', icon: '📜' },
  privacy: { slug: 'privacy', title: 'Privacy Policy', icon: '🔒' },
  disclaimer: { slug: 'disclaimer', title: 'Trading Disclaimer', icon: '⚠️' },
  howto: { slug: 'howto', title: 'How To Guides', icon: '📖' },
  faq: { slug: 'faq', title: 'Frequently Asked Questions', icon: '❓' },
  refund: { slug: 'refund', title: 'Refund Policy', icon: '💰' },
};

const DEFAULT_CONTENT: Record<string, string> = {
  about: `<h2>Welcome to ZO Bull</h2>
<p>ZO Bull is an AI-powered algorithmic trading platform connecting traders to 82+ Indian brokers and 11 cryptocurrency exchanges.</p>`,
  contact: `<h2>Get in Touch</h2>
<p>Email: support@zobull.com</p>`,
  terms: `<h2>Terms of Service</h2>
<p>Last Updated: June 2026</p>`,
  privacy: `<h2>Privacy Policy</h2>
<p>Last Updated: June 2026</p>`,
  disclaimer: `<h2>Trading Disclaimer</h2>
<p>Trading involves substantial risk of loss.</p>`,
  howto: `<h2>How To Guides</h2>
<p>Learn how to get the most out of ZO Bull.</p>`,
  faq: `<h2>Frequently Asked Questions</h2>
<p>Find answers to common questions.</p>`,
  refund: `<h2>Refund Policy</h2>
<p>7-day money-back guarantee.</p>`,
};

interface CMSPagesProps {
  page?: string;
}

export default function CMSPages({ page }: CMSPagesProps) {
  const { page: urlPage } = useParams<{ page: string }>();
  const currentPage = (page || urlPage || 'about') as string;
  const [pageData, setPageData] = useState<PageData>(DEFAULT_PAGES[currentPage] || DEFAULT_PAGES.about);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPage(currentPage);
  }, [currentPage]);

  const fetchPage = async (slug: string) => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(`/api/cms/pages/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      } else {
        // Use default if page not found in CMS
        setError(true);
        setPageData(DEFAULT_PAGES[slug] || { slug, title: slug.charAt(0).toUpperCase() + slug.slice(1) });
      }
    } catch (err) {
      setError(true);
      setPageData(DEFAULT_PAGES[slug] || { slug, title: slug.charAt(0).toUpperCase() + slug.slice(1) });
    }
    setLoading(false);
  };

  const content = error ? (DEFAULT_CONTENT[currentPage] || '<p>Content coming soon.</p>') : (pageData.content || DEFAULT_CONTENT[currentPage] || '<p>Content coming soon.</p>');

  return (
    <div className="cms-page">
      <div className="cms-container">
        <div className="cms-header">
          <Link to="/landing" className="cms-back-link">
            ← Back to Home
          </Link>
          <div className="cms-title-wrapper">
            <span className="cms-icon">{pageData.icon || '📄'}</span>
            <h1 className="cms-title">{pageData.title || currentPage}</h1>
          </div>
          {pageData.description && (
            <p className="cms-description">{pageData.description}</p>
          )}
        </div>
        
        <div className="cms-content" dangerouslySetInnerHTML={{ __html: content }} />
        
        <div className="cms-footer">
          <p>Questions? <Link to="/contact">Contact us</Link></p>
        </div>
      </div>
    </div>
  );
}

// Export for use in routes
export { DEFAULT_PAGES };
